import {RouteRecordRaw} from "vue-router";
import {dayjs} from "element-plus";
import {userUserStore} from "@/store/user";
import pinia from "@/store";
import {TestSubCate, TestSubCateSelector} from "@/types";

export const testTypeList = [
    {
        label: "组合",
        value: 0
    },
    {
        label: "单选",
        value: 1
    },
    {
        label: "多选",
        value: 2
    },
    {
        label: "填空",
        value: 3
    },
    {
        label: "简答",
        value: 4
    },
    {
        label: "竞赛",
        value: 5
    }
]

export const testSubTypeList = [
    {
        label: "单选",
        value: 1
    },
    {
        label: "多选",
        value: 2
    },
    {
        label: "填空",
        value: 3
    },
    {
        label: "简答",
        value: 4
    },
    {
        label: "竞赛",
        value: 5
    }
]

export const convertToTestSubCateSelector = (
    subCateList?: TestSubCate[]): TestSubCateSelector[] => {
    if (!subCateList) return [];
    return subCateList.map((item) => {
        const selector: TestSubCateSelector = {
            value: item.id,
            label: item.name,
        };
        if (item.list && item.list.length > 0) {
            selector.children = convertToTestSubCateSelector(item.list);
        }
        return selector;
    });
}


export const getQuestionType = (type: number) => {
    switch (type) {
        case 0:
            return "组合"
        case 1:
            return "单选"
        case 2:
            return "多选"
        case 3:
            return "填空"
        case 4:
            return "简答"
        case 5:
            return "竞赛"
        default:
            return "未知"
    }
}

// 正则判断是否含有英文大写字母以外的字符串
export const checkEnglishUpperCase = (str: string) => {
    const regex = /[^A-Z]/;
    return !regex.test(str);
}

// 正则判断是否含有T和F之外的字符串
export const checkEnglishAndTF = (str: string) => {
    const regex = /[^TF]/;
    return !regex.test(str);
}

/**
 * 获取时长
 */
export const durationList = () => {
    const list: number[] = []
    let current = 10
    while (current < 240) {
        current += 5
        list.push(current)
    }
    return list
}

export const isLogin = () => {
    const userStore = userUserStore(pinia)
    return userStore.token !== ""
}

/**
 * 检查当前用户是否有权限
 * @param authority 路由权限 [0,1]代表类型为0或者1的可以访问菜单
 */
export const isAuth = (authority: (string | number)[]) : boolean => {
    if (!authority) return true
    const userStore = userUserStore(pinia)
    return authority.includes(userStore.type)
}

/**
 * 生成路由表
 * @param _routes 定义的路由
 */
export const genRouteList = (_routes: RouteRecordRaw[]): RouteRecordRaw[] => {
    const menuData = [];
    for (let index = 0; index < _routes.length; index++) {
        const route = _routes[index];
        // @ts-ignore
        if (!isAuth(route?.meta?.authority)) {
            continue
        }
        if (route.name
            && route.meta
            && route.meta.title
            && !(route.meta && route.meta.hideMenu)) {
            const newItem = {...route};
            delete newItem.children;
            if (route.children) {
                newItem.children = genRouteList(route.children);
            }
            menuData.push(newItem);
        } else if (route.children) {
            menuData.push(...genRouteList(route.children));
        }
    }
    return menuData;
};

/**
 * 课程类型
 * @param type 课程类型 1-直播课 2-录播课 3-系列课 4-组合课
 */
export const getCourseType = (type: number) => {
    switch (type) {
        case 1:
            return "直播课"
        case 2:
            return "录播课"
        case 3:
            return "系列课"
        case 4:
            return "组合课"
        default:
            return "未知"
    }
}

/**
 * 课程状态
 * @param status 课程状态 1-已上架 2-待发布 0-已下架
 */
export const getCourseStatus = (status: number) => {
    switch (status) {
        case 1:
            return "已上架"
        case 2:
            return "待发布"
        case 0:
            return "已下架"
        default:
            return "未知"
    }
}

/**
 * 章节排序格式化(前+0)
 * @param section_sort 章节排序
 */
export const getSectionSort = (section_sort: number) => {
    return section_sort < 10 ? `0${section_sort}` : section_sort;
}

/**
 * 指定日期格式化
 * @param time 日期时间戳13位
 */
export const getTimeStr = (time: number) => {
    if (!time) return "--"
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
}

/**
 * 秒数转时分秒
 */
export const getDurationStr = (time: number) => {
    const hour = Math.floor(time / 3600);
    const minute = Math.floor((time - hour * 3600) / 60);
    const second = time - hour * 3600 - minute * 60;
    if (hour === 0) {
        if (minute === 0) {
            return `${second}秒`
        }
        return `${minute}分${second}秒`;
    }
    return `${hour}时${minute}分${second}秒`
}

/**
 * 适用于el-date-picker 选择日期范围时 获取unix时间戳
 * @param dates
 */
export const getDateInfo = (dates: Date[]) => {
    if (dates.length <= 1) {
        return {start_time: 0, end_time: 0}
    }
    return {start_time: dayjs(dates[0]).unix(), end_time: dayjs(dates[1]).unix()}
}

/**
 * 加载视频文件，获取视频宽高
 */
export const loadVideo = (file: File): Promise<HTMLVideoElement> => {
    return new Promise((resolve, reject) => {
        const videoElem = document.createElement('video')
        const dataUrl = URL.createObjectURL(file)
        // 当前帧的数据是可用的
        videoElem.onloadeddata = function () {
            resolve(videoElem)
        }
        videoElem.onerror = function () {
            reject('video 后台加载失败')
        }
        // 设置 auto 预加载数据， 否则会出现截图为黑色图片的情况
        videoElem.setAttribute('preload', 'auto')
        videoElem.src = dataUrl
    })
}

/**
 * 加载图片文件，获取能识别的base64
 */
export const loadImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        // 绑定错误处理事件
        reader.onerror = function () {
            reject('图片后台加载失败');
        };

        reader.onabort = function () {
            reject('图片后台加载失败');
        };

        // 绑定加载完成事件
        reader.onload = function () {
            if (reader.result) {
                resolve(reader.result as string);
            } else {
                reject('图片加载结果为空');
            }
        };

        // 绑定加载开始事件
        reader.onloadstart = function () {
            // 开始加载
        };

        // 绑定加载进度事件
        reader.onprogress = function () {
            // 正在加载
        };

        // 绑定加载结束事件
        reader.onloadend = function () {
            // 不需要在这里调用 URL.revokeObjectURL，因为 reader.result 可能为 null
        };

        // 读取文件为 Data URL
        reader.readAsDataURL(file);
    });
}



/**
 * 格式化文件大小
 * @param size 文件大小 单位为字节
 */
export const getFileSize = (size: number) => {
    // 获取文件大小 size 单位为字节
    if (size < 1024) {
        return size + "B";
    } else if (size < 1048576) {
        return (size / 1024).toFixed(2) + " KB";
    } else if (size < 1073741824) {
        return (size / 1048576).toFixed(2) + " MB";
    } else {
        return (size / 1073741824).toFixed(2) + " GB";
    }
}

/**
 * 获取文件后缀名
 * @param filename 文件名（一般通过file.name获取）
 */
export const getFileExt = (filename: string) => {
    // 获取文件后缀名
    const index = filename.lastIndexOf('.')
    if (index === -1) {
        return ''
    }
    return filename.slice(index + 1)
}

/**
 * 将blob对象转换为file对象
 * @param blob
 * @param fileName file文件名
 */
export const blobToFile = (blob: Blob, fileName: string): File => {
    return new File([blob], fileName, {
        type: blob.type,
    });
};

/**
 * 获取指定日期是星期几
 * @param time 日期时间戳13位
 */
export const getDayOfWeek = (time: number) => {
    const dayOfWeek = dayjs(time).day();
    const week = ["日", "一", "二", "三", "四", "五", "六"];
    return "周" + week[dayOfWeek];
};

export class TimeFormatter {

    /**
     * 格式化时间 年-月-日 时:分:秒
     */
    static formatTime = (time: string | number | Date | dayjs.Dayjs): string => {
        if (!time) return "";
        time = dayjs(time).format("YYYY-MM-DD HH:mm:ss");
        return time;
    };

    /**
     * 秒数格式化
     * @param seconds 秒
     */
    static secondsFormat = (seconds: number) => {
        // 计算秒数除以3600的商，即小时数
        const h = Math.floor(seconds / 3600);
        // 计算秒数除以60的商，即分钟数
        const minute = Math.floor((seconds / 60) % 60);
        // 计算秒数除以60的余数，即秒数
        const second = Math.ceil(seconds % 60);
        // 如果小时数小于10，则前面补0
        const hours = h < 10 ? "0" + h : h;
        // 如果秒数大于59，则取59
        const formatSecond = second > 59 ? 59 : second;
        // 返回格式化后的时间
        return `${h > 0 ? `${hours}时` : ""}${minute < 10 ? "0" + minute + "分" : minute + "分"}${
            formatSecond < 10 ? "0" + formatSecond + "秒" : formatSecond + "秒"
        }`;
    };
}


export class GroupDiscountUtil {

    static countList = [
        2, 3, 4, 5
    ]

    static discountList = [
        1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5
    ]
}

/**
 * 验证手机号
 * @param phone 手机号
 */
export const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^1[3-9]\d{9}$/;
    return phoneRegex.test(phone);
}

export function parseTextToQuestion(text: string): { title: string, options: string[] } {
    const tokens = text.trim().split(/(?=[A-Z][.、])/);

    let title = '';
    let options: string[] = [];

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i].trim();
        if (/^[A-Z][.、]/.test(token)) {
            options.push(token.replace(/^[A-Z][.、]\s*/, '').trim());
        } else {
            if (title) {
                title += ' ' + token;
            } else {
                title = token;
            }
        }
    }

    if (title.length === 0 || options.length === 0) {
        throw new Error("文本内容不足以构成一个题目和选项");
    }

    return { title, options };
}
